<template>
  <!-- Outer wrapper is focusable -->
  <div
    class="mt-1 outline-none p-1 border border-line rounded-md focus-within:ring-1 focus-within:ring-primary"
    :class="outerClass"
    tabindex="0"
    @focus="handleFocus"
    @blur="handleBlur"
    @paste="handlePaste"
  >
    <!-- Hidden input -->
    <input
      ref="fileInput"
      type="file"
      :accept="accept"
      :multiple="multiple"
      :disabled="disabled"
      class="hidden"
      @change="onFileChange"
    />

    <!-- Custom button -->
    <button
      type="button"
      @click="$refs.fileInput.click()"
      :class="[
        'file-btn px-4 py-2 text-sm font-semibold rounded-full border',
        disabled
          ? 'bg-surface-3 text-ink-mute cursor-not-allowed'
          : 'bg-primary-light text-primary hover:opacity-90'
      ]"
      :disabled="disabled"
    >
      {{ label }}
    </button>

   <span
  v-if="selectedFile"
  class="ml-2 text-sm text-ink-soft max-w-[200px] truncate inline-block align-middle"
  :title="multiple ? selectedFile.map(f => f.name).join(', ') : selectedFile.name"
>
  {{ multiple ? selectedFile.map(f => f.name).join(', ') : selectedFile.name }}
</span>
<span v-else class="ml-2 text-sm text-ink-mute">No file chosen</span>
  </div>
</template>

<script setup>
import { ref } from "vue";

const props = defineProps({
  accept: { type: String, default: "" },
  fieldName: { type: String, required: true },
  index: { type: Number, default: null },
  disabled: { type: Boolean, default: false },
  multiple: { type: Boolean, default: false },
  outerClass: { type: String, default: "" },
  /** What the button says (AB#21538: "Attach PO document" on the manual order form). */
  label: { type: String, default: "Choose File" }
});

const emit = defineEmits(["file-change", "focus", "blur"]);

const fileInput = ref(null);
const selectedFile = ref(null);

const onFileChange = (event) => {
  const files = event.target.files;
  if (!files || files.length === 0) return;

  selectedFile.value = props.multiple ? [...files] : files[0];

  emit("file-change", {
    files: props.multiple ? files : files,
    fieldName: props.fieldName,
    index: props.index,
  });
};

const handleFocus = () => {
  emit("focus", { fieldName: props.fieldName, index: props.index });
};

const handleBlur = () => {
  emit("blur", { fieldName: props.fieldName, index: props.index });
};

const handlePaste = (event) => {

  try {
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    if (event.stopPropagation) event.stopPropagation();
    if (event.preventDefault) event.preventDefault();
  } catch (e) {
    // ignore
  }

  const clipboardItems = event.clipboardData?.items;
  if (!clipboardItems) return;

  const files = [];
  for (let i = 0; i < clipboardItems.length; i++) {
    const item = clipboardItems[i];
    if (item.kind === "file") {
      const f = item.getAsFile();
      if (f) files.push(f);
    }
  }

  if (files.length > 0) {
    selectedFile.value = props.multiple ? files : files[0];
    emit("file-change", {
      files: props.multiple ? files : files,
      fieldName: props.fieldName,
      index: props.index,
    });
  }
};
</script>

<style scoped>
.file-btn {
  @apply border-0;
}
</style>
